<c-container fluid>
  <c-row>
    <c-col xs="12">
      <c-card class="mb-4">
        <c-card-header class="d-flex align-items-center gap-2">
          <strong>{{ 'profile.settings' | translate }}</strong>
          <!-- Founding Member: shown on the member's own profile as a permanent
               distinction. Renders nothing for everyone else. -->
          <founding-member-badge [foundingMember]="foundingMember()"
                                 [memberNumber]="foundingMemberNumber()" />
          <founding-member-badge kind="practitioner"
                                 [foundingMember]="foundingPractitioner()"
                                 [memberNumber]="foundingPractitionerNumber()" />
        </c-card-header>

        <c-card-body>
          <!-- Email Verification Alert -->
          @if (!emailVerified()) {
            <c-alert color="warning" dismissible class="mb-3">
              <strong>{{ 'profile.email_verification_required' | translate }}</strong>
              <p class="mb-2">{{ 'profile.email_verification_message' | translate }}</p>
              <button
                cButton
                color="warning"
                size="sm"
                (click)="resendVerificationEmail()"
                [disabled]="resendingEmail()"
              >
                {{ resendingEmail() ? 'Sending...' : 'Resend Verification Email' }}
              </button>
            </c-alert>
          }

          <!-- Success / Error Message -->
          @if (message(); as msg) {
            <div class="alert" [ngClass]="msg.type === 'success' ? 'alert-success' : 'alert-danger'">
              {{ msg.text }}
            </div>
          }

          <c-tabs [activeItemKey]="activeTab()">
            <c-tabs-list variant="tabs">
              <button cTab [itemKey]="0">Personal Info</button>
              <button cTab [itemKey]="1">Address</button>
              <button cTab [itemKey]="2">Security</button>
              @if (communityEnabled()) {
                <button cTab [itemKey]="3">{{ 'community.title' | translate }}</button>
              }
            </c-tabs-list>

            <c-tabs-content>
              <!-- Personal Info -->
              <c-tab-panel [itemKey]="0" class="p-3">
                <form [formGroup]="profileForm" (ngSubmit)="saveProfile()">
                  <c-row class="mb-3">
                    <c-col md="6">
                      <label cLabel for="first_name">{{ 'profile.first_name' | translate }}</label>
                      <input cFormControl formControlName="first_name" id="first_name" type="text" />
                    </c-col>
                    <c-col md="6">
                      <label cLabel for="last_name">{{ 'profile.last_name' | translate }}</label>
                      <input cFormControl formControlName="last_name" id="last_name" type="text" />
                    </c-col>
                  </c-row>

                  <c-row class="mb-3">
                    <c-col md="6">
                      <label cLabel for="email">{{ 'profile.email' | translate }}</label>
                      <input cFormControl formControlName="email" id="email" type="email" required />
                      <small class="text-body-secondary">{{ 'profile.email_change_note' | translate }}</small>
                    </c-col>
                    <c-col md="6">
                      <label cLabel for="mobile">{{ 'profile.mobile' | translate }}</label>
                      <input cFormControl formControlName="mobile" id="mobile" type="tel" />
                    </c-col>
                  </c-row>

                  <c-row class="mb-3">
                    <c-col md="6">
                      <label cLabel for="dob">{{ 'profile.dob' | translate }}</label>
                      <input cFormControl formControlName="dob" id="dob" type="date" />
                    </c-col>
                    <c-col md="6">
                      <label cLabel for="gender">{{ 'profile.gender' | translate }}</label>
                      <c-multi-select
                        formControlName="gender"
                        [multiple]="false"
                        selectionType="text"
                        placeholder="{{ 'profile.select_gender' | translate }}"
                        [cleaner]="true"
                      >
                        @for (gender of genders; track gender.value) {
                          <c-multi-select-option [value]="gender.value">
                            {{ gender.label }}
                          </c-multi-select-option>
                        }
                      </c-multi-select>
                    </c-col>
                  </c-row>

                  <div class="d-flex justify-content-end">
                    <button
                      cButton
                      color="primary"
                      type="submit"
                      [disabled]="loading() || profileForm.invalid"
                    >
                      {{ loading() ? 'Saving...' : 'Save Changes' }}
                    </button>
                  </div>
                </form>
              </c-tab-panel>

              <!-- Address -->
              <c-tab-panel [itemKey]="1" class="p-3">
                <form [formGroup]="profileForm" (ngSubmit)="saveProfile()">
                  <c-row class="mb-3">
                    <c-col xs="12">
                      <label cLabel for="address">{{ 'profile.address_line1' | translate }}</label>
                      <input cFormControl formControlName="address" id="address" type="text" />
                    </c-col>
                  </c-row>

                  <c-row class="mb-3">
                    <c-col xs="12">
                      <label cLabel for="address2">{{ 'profile.address_line2' | translate }}</label>
                      <input cFormControl formControlName="address2" id="address2" type="text" />
                    </c-col>
                  </c-row>

                  <c-row class="mb-3">
                    <c-col md="6">
                      <label cLabel for="city">{{ 'profile.city' | translate }}</label>
                      <input cFormControl formControlName="city" id="city" type="text" />
                    </c-col>
                    <c-col md="6">
                      <label cLabel for="postcode">{{ 'profile.postcode' | translate }}</label>
                      <input cFormControl formControlName="postcode" id="postcode" type="text" />
                    </c-col>
                  </c-row>

                  <c-row class="mb-3">
                    <c-col md="6">
                      <label cLabel for="country_code">{{ 'profile.country' | translate }}</label>
                      <c-multi-select
                        formControlName="country_code"
                        [multiple]="false"
                        selectionType="text"
                        placeholder="{{ 'profile.select_country' | translate }}"
                        [search]="true"
                        searchNoResultsLabel="{{ 'profile.no_countries_found' | translate }}"
                        [cleaner]="true"
                      >
                        @for (country of filteredCountries(); track country.value) {
                          <c-multi-select-option [value]="country.value">
                            {{ country.label }}
                          </c-multi-select-option>
                        }
                      </c-multi-select>
                    </c-col>

                    <c-col md="6">
                      <label cLabel for="state_code">{{ 'profile.state' | translate }}</label>
                      <c-multi-select
                        formControlName="state_code"
                        [multiple]="false"
                        selectionType="text"
                        placeholder="{{ 'profile.select_state' | translate }}"
                        [search]="true"
                        searchNoResultsLabel="{{ 'profile.no_states_found' | translate }}"
                        [cleaner]="true"
                        [disabled]="!hasStates()"
                      >
                        @for (state of selectedCountryStates(); track state.value) {
                          <c-multi-select-option [value]="state.value">
                            {{ state.label }}
                          </c-multi-select-option>
                        }
                      </c-multi-select>
                    </c-col>
                  </c-row>

                  <div class="d-flex justify-content-end">
                    <button
                      cButton
                      color="primary"
                      type="submit"
                      [disabled]="loading() || profileForm.invalid"
                    >
                      {{ loading() ? 'Saving...' : 'Save Changes' }}
                    </button>
                  </div>
                </form>
              </c-tab-panel>

              <!-- Security -->
              <c-tab-panel [itemKey]="2" class="p-3">
                <form [formGroup]="passwordForm" (ngSubmit)="changePassword()">
                  <c-row class="mb-3">
                    <c-col md="8">
                      <label cLabel for="current_password">{{ 'profile.current_password' | translate }}</label>
                      <input
                        cFormControl
                        formControlName="current_password"
                        id="current_password"
                        type="password"
                        required
                      />
                    </c-col>
                  </c-row>

                  <c-row class="mb-3">
                    <c-col md="8">
                      <label cLabel for="password">{{ 'profile.new_password' | translate }}</label>
                      <input
                        cFormControl
                        formControlName="password"
                        id="password"
                        type="password"
                        required
                      />
                      <small class="text-body-secondary">{{ 'profile.password_min_length' | translate }}</small>
                    </c-col>
                  </c-row>

                  <c-row class="mb-3">
                    <c-col md="8">
                      <label cLabel for="password_confirmation">{{ 'profile.confirm_new_password' | translate }}</label>
                      <input
                        cFormControl
                        formControlName="password_confirmation"
                        id="password_confirmation"
                        type="password"
                        required
                      />
                    </c-col>
                  </c-row>

                  <div class="d-flex justify-content-end">
                    <button
                      cButton
                      color="primary"
                      type="submit"
                      [disabled]="loading() || passwordForm.invalid"
                    >
                      {{ loading() ? 'Changing...' : 'Change Password' }}
                    </button>
                  </div>
                </form>

                <!-- Delete Account -->
                <hr class="my-4" />

                <div class="delete-account-section">
                  <h6 class="text-danger mb-3">{{ 'delete_account.title' | translate }}</h6>
                  <p class="text-muted small mb-3">{{ 'delete_account.warning_message' | translate }}</p>
                  <button
                    cButton
                    color="danger"
                    variant="outline"
                    (click)="navigateToDeleteAccount()"
                  >
                    {{ 'delete_account.button' | translate }}
                  </button>
                </div>
              </c-tab-panel>

              <!-- Community Network profile (§6/§7) -->
              @if (communityEnabled()) {
                <c-tab-panel [itemKey]="3" class="p-3">
                  <app-community-profile-section />
                </c-tab-panel>
              }
            </c-tabs-content>
          </c-tabs>
        </c-card-body>
      </c-card>
    </c-col>
  </c-row>
</c-container>